import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Dialog, IconButton, Typography } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';

import { useBillingStore, useUserStore } from '../../../../../../store/models';
import { getAddOns } from '../../../../../../conf/chargify';
import CheckoutDrawer from '../../../../../../components/CheckoutDrawer';
import useCheckoutState from '../../../../../../components/CheckoutDrawer/useCheckoutState';
import CtaButton from '../../../../../../commons/ctaButton';
import mapPlanToCard from '../../../../../../components/Billing/mapPlanToCard';
import AddOnsList from './sections/AddOnsList';
import { buildDefaults } from './addOnValues';
import { useStyles } from './addOnsDialog.styles';

const TITLE_ID = 'ecal-add-ons-dialog-title';

function AddOnsDialog ({ open, onClose, onPaymentSuccess }) {
    const classes = useStyles();
    const billingStore = useBillingStore();
    const userStore = useUserStore();

    const { checkoutOpen, selectedPlan, selectedAddOnValues, handleChoosePlan, closeCheckout } = useCheckoutState();
    const [addOnValues, setAddOnValues] = useState({});

    // Stays on the current plan and only changes add-ons — unlike the reused
    // CheckoutDrawer's default (a new subscription) and UpgradeECALPlanDialog's
    // override (a plan change), this calls updateSubscription so the account's
    // existing plan/add-ons are updated in place.
    async function addExtraServices (token) {
        const res = await billingStore.updateSubscription({
            code: selectedPlan.code,
            token,
            addOns: getAddOns(selectedAddOnValues, { addOns: selectedPlan.addOns || [] }),
        });
        if (res.is3DSecure) {
            return { is3DSecure: true };
        }
        if (res.hasCardError) {
            return { error: res.chargifyErr || 'Payment failed. Please check your card details and try again.' };
        }
        return {};
    }

    // Finalizes a 3D-Secure-challenged subscription once the publisher completes
    // authentication — same params shape addExtraServices() already builds for
    // billingStore.updateSubscription().
    async function finalizeThreeDSecure ({ token, form }) {
        return billingStore.finalize3DSecure({
            code: selectedPlan.code,
            addOns: getAddOns(selectedAddOnValues, { addOns: selectedPlan.addOns || [] }),
            token,
            chargifyInput: {
                firstName: form.firstName,
                lastName: form.lastName,
            },
        });
    }

    async function previewAddOns (input) {
        billingStore.coupon = (input.couponCodes && input.couponCodes[0]) || '';
        const res = await billingStore.getSubscriptionPreview({
            code: input.code,
            addOns: input.addOns,
            chargifyInput: {
                country: input.country,
                state: input.state,
                zip: input.zip,
                city: input.city,
            },
        });
        if (!res || !res.billingPreview) {
            return { error: true };
        }
        return { data: res.billingPreview };
    }

    useEffect(() => {
        billingStore.getPlanDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const plansResolved = !!billingStore.observed.planDetails;
    const currentPlan = billingStore.publisherPlan;
    const currency = currentPlan && currentPlan.currency;
    const hasEnabledAddOn = Object.values(addOnValues).some(Boolean);

    // Resolves the current plan's catalog entry (add-ons definitions, price,
    // description) rather than relying on publisherPlan, which only carries the
    // account's currently active add-ons, not the full purchasable list.
    const planCard = useMemo(() => {
        const source = (billingStore.fullPlans && billingStore.fullPlans.length)
            ? billingStore.fullPlans
            : (billingStore.planDetails || []);
        const match = source.find((plan) => plan.id === (currentPlan && currentPlan.id));
        return match ? mapPlanToCard(match, currency) : null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billingStore.observed.planDetails, currentPlan, currency]);

    // Seeds quantity add-ons to 0 and toggles to false once the plan's add-ons
    // are known — without this, addOnValues stays {} and the quantity Select
    // renders with a value that matches none of its options.
    useEffect(() => {
        if (planCard) {
            setAddOnValues(buildDefaults(planCard.addOns || []));
        }
    }, [planCard]);

    function updateAddOnValue (id, value) {
        setAddOnValues((prev) => ({ ...prev, [id]: value }));
    }

    return (
        <React.Fragment>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={false}
                fullWidth
                aria-labelledby={TITLE_ID}
                PaperProps={{ className: classes.paper }}
                BackdropProps={{
                    style: {
                        backgroundColor: 'rgba(10,14,40,0.45)',
                        backdropFilter: 'blur(3px)',
                    },
                }}
            >
                <div className={classes.header}>
                    <div>
                        <Typography id={TITLE_ID} className={classes.title}>
                            Add Extra Services
                        </Typography>
                        {currentPlan && currentPlan.name && (
                            <Typography className={classes.subtitle}>
                                Currently on <strong>{currentPlan.name}</strong>
                            </Typography>
                        )}
                    </div>
                    <IconButton
                        aria-label="Close"
                        className={classes.closeButton}
                        onClick={onClose}
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>

                <div className={classes.body}>
                    {planCard && (
                        <React.Fragment>
                            <Typography className={classes.planName}>
                                {`ECAL ${planCard.name} Plan Add Ons`}
                            </Typography>
                            {planCard.description && (
                                <Typography variant='body2' className={classes.planDescription}>
                                    {planCard.description}
                                </Typography>
                            )}

                            <AddOnsList
                                addOns={planCard.addOns}
                                currency={currency}
                                values={addOnValues}
                                onChange={updateAddOnValue}
                            />

                            <CtaButton
                                kind='primary'
                                className={classes.cta}
                                fullWidth
                                disabled={!plansResolved || !hasEnabledAddOn}
                                onClick={() => handleChoosePlan(planCard, addOnValues)}
                            >
                                NEXT
                            </CtaButton>
                        </React.Fragment>
                    )}
                </div>
            </Dialog>

            <CheckoutDrawer
                open={checkoutOpen}
                plan={selectedPlan}
                addOnValues={selectedAddOnValues}
                onClose={closeCheckout}
                subscribeAction={addExtraServices}
                // subscriberLimit is sourced solely from the publisher call (EBL-12287),
                // so it must be refreshed here too or the usage bar shows a stale limit
                // right after purchasing an add-on.
                onSubscribed={async () => {
                    await billingStore.getCurrentPlan();
                    await userStore.authGetPublisherInfo({
                        cognitoToken: userStore.env.userCognitoToken,
                        navigate: false,
                        updateUserInfoLoaded: false,
                    });
                }}
                previewAction={previewAddOns}
                threeDSecureStore={billingStore}
                finalize3DSecureAction={finalizeThreeDSecure}
                addOnsOnly
                showBasePrice={false}
                showPromo={false}
                title={selectedPlan ? `Add services to ${selectedPlan.name}` : ''}
                productLabel=''
                payLabel='ADD SERVICES'
                onSuccess={() => {
                    closeCheckout();
                    onClose();
                    if (onPaymentSuccess) {
                        onPaymentSuccess('Add-Ons Upgraded!', 'Congratulations! Your have upgraded your add ons.');
                    }
                }}
            />
        </React.Fragment>
    );
}

AddOnsDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onPaymentSuccess: PropTypes.func,
};

const ObservedAddOnsDialog = observer(AddOnsDialog);
export default ObservedAddOnsDialog;
